import type { useQuizResultActions } from '@mentimeter/quiz';
import {
  GameStateEnums,
  type GameStates,
  type Player,
  type QuizPlayerScore,
} from '@mentimeter/quiz-types';
import type { Layout } from '@mentimeter/http-clients';
import { RevealAnswer } from '../../shared/components/quiz/RevealAnswer';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { Results } from './results/Results';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QuizSlideState = {
  started?: boolean;
  starting?: boolean;
};

export interface GameSatesProps {
  gameState: GameStates | undefined;
  timeOffset: number;
  connectedPlayers: Player[];
  respondents: string[];
  onReload: () => void;
  onMarkAnswer:
    | ReturnType<typeof useQuizResultActions>['markAnswer']
    | undefined;
  everyoneHasVoted: boolean;
  connectedWithDelay: boolean;
}

export const GameStatesSwitcher = ({
  correctAnswer,
  imageUrl,
  timeBasedScoring,
  question,
  questionStyled,
  meta,
  description,
  layout,
  isReadonly,
  results,
  isPreviewResults,
  gameState,
  timeOffset,
  connectedPlayers,
  respondents,
  onReload,
  everyoneHasVoted,
  connectedWithDelay,
  totalQuizQuestions,
  currentQuizQuestionIndex,
  onMarkAnswer,
}: {
  correctAnswer: string | undefined;
  imageUrl: string | undefined;
  timeBasedScoring: boolean;
  question: string;
  questionStyled: Record<string, unknown>;
  meta: string;
  description: string;
  layout: Layout | undefined;
  isReadonly: boolean;
  results: QuizPlayerScore[];
  isPreviewResults: boolean;
  totalQuizQuestions: number;
  currentQuizQuestionIndex: number;
} & GameSatesProps) => {
  const shouldShowLegacyImage = layout?.type === 'default';

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={question}
          styledTitle={questionStyled}
          label={meta}
          fontSizeOffset={layout?.font_size_offset}
        />
        {results.length > 0 && (
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results
              onMarkAnswer={onMarkAnswer}
              results={results}
              isReadonly={isReadonly}
            />
          </RevealAnswer>
        )}
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalQuizQuestions}
            current={currentQuizQuestionIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={timeBasedScoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={questionStyled}
            label={meta}
            description={description}
            fontSizeOffset={layout?.font_size_offset}
          />
          <CenteringContainer>
            <Countdown
              type="open"
              respondents={respondents.length}
              everyoneHasVoted={everyoneHasVoted}
              imageUrl={imageUrl}
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={questionStyled}
            label={meta}
            fontSizeOffset={layout?.font_size_offset}
          />
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results
              onMarkAnswer={onMarkAnswer}
              results={results}
              isReadonly={isReadonly}
            />
          </RevealAnswer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading onReload={onReload} />
        </CenteringContainer>
      );
    }
  }
};
